import formatDate from 'dateformat';
import { MATFILE_ENDLESS_TYPE } from 'pxc-editor-core/constants';
import { createSectionSmart, generatePage, applyPageWidthToPage } from 'pxc-editor-core/helpers/schema';
import { safeAtob } from 'pxc-editor-core/helpers/common';
import { getColorByVariantRibbonColor as getColorByMaterialVariantRibbonColor } from "pxc-editor-core/helpers/material";
import { ENDLESS_FIXED, ENDLESS_BASED_ON_CONTENT, ENGRAVING, NO_PRINTER_ID, SECTION_CONTENT_TYPE, DEFAULT_CONTRAST_VALUE } from '@/constants';
import { setWizardData } from '@/actions';
import { getMaterialWithLaserParameters } from '@/helpers';
import { getBuildedMatfileTemplate, parseMatfileToBasicVersion } from '@/helpers/matfile';
import { convertPercentToValue } from '@/helpers/graphicContent';
import { fromProjFileContent as barcodeFromProjFileContent } from './barcodeInOutMapper';

let originInitFullProjectData = null;
let initProjectData = null;
let originMatFile = null;
let originMatFileMetadata = null;
let originWizardPrintOptions = null;
let originPagesSeparators = null;
let createdDate = null;
let selWizard = null;

export const getSelWizard = () => {
  return selWizard;
}

export const setSelWizard = (wizard) => {
  selWizard = wizard;
  return selWizard;
}

export const getOriginMatFile = () => {
  return originMatFile;
};

export const getOriginMatFileMetadata = () => {
  return originMatFileMetadata;
};

export const getOriginInitFullProjectData = () => {
  return originInitFullProjectData;
};

export const getInitProjectData = () => {
  return initProjectData;
};

export const getOriginWizardPrintOptions = () => {
  return originWizardPrintOptions;
};

export const getOriginPagesSeparators = () => {
  return originPagesSeparators;
};

export const getCreatedDate = () => {
  return createdDate;
};

export const parseInitProjectData = (data) => {
  originInitFullProjectData = data;

  createdDate = formatDate(new Date(), 'isoDateTime');

  const {
    material,
    material_metadata,
    material_selected_article_number,
    wizard,
    project,
    file_name,
    project_metadata
  } = originInitFullProjectData;

  const matFileJson = material?.is_base64 ? safeAtob(material?.contents) : material?.contents;
  const _originMatFile = JSON.parse(matFileJson);

  originMatFile = _originMatFile?.material ? _originMatFile : { material: _originMatFile };
  originMatFileMetadata = null;
  initProjectData = null;
  originWizardPrintOptions = null;
  
  let wizard_material_selected_article_number = null;

  if (material_metadata) {
    const matFileMetaJson = material_metadata.is_base64 ? safeAtob(material_metadata.contents) : material_metadata.contents;
    originMatFileMetadata = JSON.parse(matFileMetaJson);
  }

  const printerIds = (originMatFile?.material
    ? originMatFile?.material?.general?.printers || originMatFile?.material?.general?.printer_types
    : originMatFile?.general?.printers || originMatFile?.general?.printer_types) || [];
  const selPrinter = getSelectedPrinter({ project_metadata });

  parseMatfileToBasicVersion(originMatFile, selPrinter);

  let projectData = null;
  let wizardData = null;
  let selWizard = null;

  if (wizard) {
    const wizardScopeData = JSON.parse(safeAtob(wizard));
    if (wizardScopeData) {
      if (wizardScopeData?.application_type === 'CABLE_FLAG') {
        selWizard = wizardScopeData;
        setSelWizard(wizardScopeData);
        setWizardData(wizardScopeData);
      }
      // IMPORTANT to support different structures of data
      wizardData = wizardScopeData.editor_data? wizardScopeData.editor_data : wizardScopeData;
      originWizardPrintOptions = wizardScopeData.print_options;
      wizard_material_selected_article_number = wizardScopeData.material?.article;
    }
  }

  if (project) {
    initProjectData = JSON.parse(safeAtob(project));
    projectData = parseProjectOuterData(initProjectData, selWizard);
  }

  const materialWithLaserParameters = getMaterialWithLaserParameters();
  const defaultLaserOutputQualityValue = materialWithLaserParameters.$2 ? '' : ENGRAVING;
  const laserOutputQuality = initProjectData?.project?.global_laser_settings?.laser_output_quality || defaultLaserOutputQualityValue;
  const matfileTemplate = getBuildedMatfileTemplate();

  return {
    matfileTemplate,
    project: projectData,
    wizard: wizardData,
    selectedVariantArticleNumber: wizard_material_selected_article_number || material_selected_article_number,
    file_name,
    selPrinter,
    printerList: printerIds,
    laserOutputQuality,
  };
};

const parseProjectOuterData = (projectOuterData, selWizard) => {
  const { variants, type: matfileType } = getBuildedMatfileTemplate();
  const {
    project: {
      text_styles,
      created,
      materials,
    },
  } = projectOuterData;

  createdDate = created;

  const {
    variant_article,
    separators,
    pages, //, invert_colors
  } = materials[0]; // now there always one item

  originPagesSeparators = separators || null;

  const res = {
    pages: {},
    colorSchema: null,
    usableColors: {},
    globalProjectSettings: parseGlobalProjectSettings(projectOuterData)
  };

  // *** set colorSchema ******
  if (variants && variants.length > 0) {
    res.colorSchema = variants.find(variant => variant.article_number === variant_article) || variants[0];
  }

  // collect posible custom colors. use dict for uniqueness
  const usableColors = {
    fontColors: {},
    sectionFillColors: {},
    symbolStrokeColors: {},
    symbolFillColors: {},
  };
  // *** set pages ******
  pages.forEach(({ id, endless_options, labels, transforms }) => {
    let resPage = generatePage({ material: getBuildedMatfileTemplate(), selWizard, id });
    labels.forEach(({ group_id, shield_id, is_matrix, is_fixed_matrix, is_matrix_borders_enabled, sections }) => {
        sections.forEach(rawSection => {
          const shield = getShield(resPage.layouts, group_id, shield_id);
          if (!shield) {
            console.error('parseProjectOuterData: not found shield for label');
            return;
          }

          shield.isMatrix = is_matrix;
          shield.isMatrixFixedCellWidth = is_fixed_matrix;
          shield.isMatrixBordersEnabled = is_matrix_borders_enabled;

          const ribbonColor = getColorByMaterialVariantRibbonColor(res.colorSchema);
          const newSection = createSectionSmart({ sourceShield: shield, pages: { [resPage.uid]: resPage }, ribbonColor });
          const scalePercentage = rawSection?.graphic_contents_options?.scale_percentage;

          if (rawSection.contour?.rect) {
            const promProps = rawSection.contour?.rect;
            newSection
              .setX(promProps.x)
              .setY(promProps.y)
              .setWidth(promProps.w)
              .setHeight(promProps.h)
              .setChanged(is_matrix || rawSection.isChanged)
            if (promProps.background_style) {
              newSection
                .setBackgroundState(promProps.background_style.enabled)
                .setFillColor(promProps.background_style.fill_color);

              usableColors.sectionFillColors[
                promProps.background_style.fill_color
              ] = 1;
            }
          }

          let sectionTransforms = null;

          switch (rawSection.type) {
            case SECTION_CONTENT_TYPE.BARCODE:
              newSection.setCode(
                barcodeFromProjFileContent(rawSection.barcode_contents),
              );

              if (rawSection.barcode_contents.style) {
                if (rawSection.barcode_contents.style.color) {
                  newSection.setBarcodeColor(
                    rawSection.barcode_contents.style.color,
                  );
                }
                sectionTransforms =
                  rawSection.barcode_contents.style.transforms;
              }
              break;
            case SECTION_CONTENT_TYPE.IMAGE:
              newSection.setImage({
                href: rawSection.image_contents.image_href,
                source: rawSection.image_contents.image_source, // this field is not in the schema
                name: rawSection.image_contents.image_file_name,
                verticalAlignment: rawSection.image_contents.vertical_alignment,
                horizontalAlignment: rawSection.image_contents.horizontal_alignment,
                x: rawSection.image_contents.x,
                y: rawSection.image_contents.y,
                contrast: !!rawSection.image_contents.style?.contrast?.enabled, // this field is not in the schema
                contrastValue: rawSection.image_contents.style?.contrast?.value || `${DEFAULT_CONTRAST_VALUE}`, // this field is not in the schema
              });

              sectionTransforms = rawSection.image_contents.style?.transforms;
              break;
            case SECTION_CONTENT_TYPE.SYMBOL:
              newSection
                .setSymbolFont(rawSection.symbol_contents.symbol_font)
                .setGlyphFillColor(rawSection.symbol_contents.style.color)
                .setGlyph({
                  unicode: rawSection.symbol_contents.symbol,
                  verticalAlignment: rawSection.symbol_contents.vertical_alignment,
                  horizontalAlignment: rawSection.symbol_contents.horizontal_alignment,
                });

              sectionTransforms = rawSection.symbol_contents.style.transforms;
              usableColors.symbolStrokeColors[
                rawSection.symbol_contents.style.outline_color
              ] = 1;
              usableColors.symbolFillColors[
                rawSection.symbol_contents.style.color
              ] = 1;
              break;
            case SECTION_CONTENT_TYPE.TEXT:
              const textStyles = text_styles.find(
                ts => ts.id === rawSection.text_contents.style_id,
              );
              newSection.setText(rawSection.text_contents.text);
              if (textStyles) {
                newSection
                  .setFont(textStyles.font_name)
                  .setFontSize(textStyles.text_height)
                  .setItalic(textStyles.italic)
                  .setBold(textStyles.bold)
                  .setUnderline(textStyles.underline)
                  .setFontColor(textStyles.color)
                  .setVerticalAlignment(
                    textStyles.vertical_alignment.toLowerCase(),
                  )
                  .setHorizontalAlignment(
                    textStyles.horizontal_alignment.toLowerCase(),
                  );

                sectionTransforms = textStyles.transforms;
                usableColors.fontColors[textStyles.color] = 1;
              }
              break;
            case 'SEPARATOR':
              newSection.setType('separator');
              break;
            case SECTION_CONTENT_TYPE.SHAPE:
              newSection.setShape({
                name: rawSection.shape_contents.shape_type,
                softEdges: rawSection.shape_contents.outline_style.corners === 'ROUND',
                cornerRadius: rawSection.shape_contents.outline_style.round_corners_radius,
                hasStroke: rawSection.shape_contents.outline_style.enabled,
                strokeWidth: rawSection.shape_contents.outline_style.line_width,
                isDashed: rawSection.shape_contents.outline_style.line_type === 'DASHED',
                isFilled: rawSection.shape_contents.background_style.enabled,
              })
              .setShapeStokeColor(rawSection.shape_contents.outline_style.color)
              .setShapeFillColor(rawSection.shape_contents.background_style.fill_color);

              break;
          }

          if (sectionTransforms) {
            newSection
              .setAngle(sectionTransforms.angle || 0)
              .setMirrorX(!!sectionTransforms.mirror_x)
              .setMirrorY(!!sectionTransforms.mirror_y);
          }

          if (rawSection.laser_options) {
            newSection.setLaserOutputQuality({ cutout: rawSection.laser_options.cut_out_contour });
          }

          if (scalePercentage) {
            newSection.setGraphicFormattingScale(
              convertPercentToValue(scalePercentage * 100)
            );
          } else {
            newSection.setGraphicFormattingScale(1);
          }

          resPage.sections[newSection.id] = newSection.toDict();
        });
      }
    );

    if (transforms) {
      resPage.transforms = { ...transforms };
    }

    if (matfileType === MATFILE_ENDLESS_TYPE && endless_options) {
      resPage.resizeType = endless_options.adaptive_length
        ? ENDLESS_BASED_ON_CONTENT
        : ENDLESS_FIXED;
      resPage = applyPageWidthToPage(endless_options.custom_length, resPage, selWizard);
    }

    res.pages[resPage.uid] = resPage;
  });

  for (const clrGroup in usableColors) {
    res.usableColors[clrGroup] = Object.keys(usableColors[clrGroup]);
  }

  return res;
};

const parseGlobalProjectSettings = (data) => {
  const { project: { global_settings, text_styles } } = data;
  const result = { section: {}, page: {} };
  const globalSectionTextSettings = (text_styles || []).find(item => {
    return item.id === global_settings?.last_used_text_style_id;
  });

  if (globalSectionTextSettings) {
    result.section = globalSectionTextSettings;
  }
  if (global_settings?.endless_page_default_length) {
    result.page.resizeType = global_settings.endless_page_default_is_adaptive_length ? ENDLESS_BASED_ON_CONTENT : ENDLESS_FIXED;
  }
  if (global_settings?.endless_page_default_length) {
    result.page.length = global_settings.endless_page_default_length;
  }

  return result;
};

const getShield = (layouts, group_orig_id, shield_id) => {
  for (const layoutUid in layouts) {
    const { groups } = layouts[layoutUid];
    for (const groupUid in groups) {
      const { shields, id: originGroupId } = groups[groupUid];
      if (originGroupId === group_orig_id) {
        for (const shieldUId in shields) {
          const shield = shields[shieldUId];
          if (shield.shield_id === shield_id) {
            return shield;
          }
        }
      }
    }
  }
};

const getSelectedPrinter = ({ project_metadata }) => {
  if (project_metadata?.preselectedPrinter >= NO_PRINTER_ID) {
    return project_metadata.preselectedPrinter;
  }
  return NO_PRINTER_ID;
};
